import React, { useState, useEffect } from 'react';
import { getCourses, getAdminFlashSales, createFlashSale, deleteFlashSale } from '../../services/api';

function FlashSaleManager() {
  const [courses, setCourses] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [flashSalePrice, setFlashSalePrice] = useState('');
  
  const [activeSale, setActiveSale] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch initial data
  const fetchData = async () => {
    try {
      const coursesRes = await getCourses(true);
      if (coursesRes.success) {
        setCourses(coursesRes.data);
      }
      
      const salesRes = await getAdminFlashSales();
      if (salesRes.success) {
        setHistory(salesRes.data);
        const active = salesRes.data.find(s => s.active && new Date(s.end_time) > new Date());
        setActiveSale(active || null);
      }
    } catch (err) {
      console.error('Error fetching flash sale manager data:', err);
      setError('Failed to fetch data from backend. If you logged in with admin credentials, please logout and log back in to refresh your admin token.');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update dynamic countdown
  useEffect(() => {
    if (!activeSale) return;

    const updateCountdown = () => {
      const end = new Date(activeSale.end_time);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
        setActiveSale(null);
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining({ hours, minutes, seconds });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeSale]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!selectedCourseId || !flashSalePrice) {
      setError('Please select a course and enter a flash sale price.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await createFlashSale({
        courseId: parseInt(selectedCourseId),
        flashSalePrice: parseFloat(flashSalePrice)
      });

      if (response.success) {
        setSuccess('E-Learn Flash Sale successfully activated!');
        setSelectedCourseId('');
        setFlashSalePrice('');
        await fetchData();
      } else {
        setError(response.message || 'Failed to activate flash sale.');
      }
    } catch (err) {
      setError('An error occurred while creating the flash sale.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel the active flash sale?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await deleteFlashSale();
      if (response.success) {
        setSuccess('Active flash sale successfully deactivated.');
        setActiveSale(null);
        await fetchData();
      } else {
        setError(response.message || 'Failed to deactivate flash sale.');
      }
    } catch (err) {
      setError('An error occurred while cancelling the flash sale.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 animate-[fadeIn_0.5s_ease-in-out]">
      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-4 rounded-xl border border-red-100 dark:border-red-800 flex items-center gap-3">
          <i className="fa-solid fa-circle-exclamation text-lg"></i>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800 flex items-center gap-3 animate-pulse">
          <i className="fa-solid fa-circle-check text-lg"></i>
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form and Countdown Columns */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Form Card */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
              <i className="fa-solid fa-plus-circle text-emerald-600"></i>
              Launch New Flash Sale
            </h3>
            
            <form onSubmit={handleActivate} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Course *</label>
                <select
                  value={selectedCourseId}
                  onChange={(e) => setSelectedCourseId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-sm"
                >
                  <option value="">-- Choose a Course --</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title} (Current: ₹{course.price})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Flash Sale Discount Price (₹) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 499"
                  value={flashSalePrice}
                  onChange={(e) => setFlashSalePrice(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-sm"
                />
              </div>

              <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-4 rounded-xl text-xs text-emerald-800 dark:text-emerald-300 border border-emerald-100/50 dark:border-emerald-900/50">
                <i className="fa-solid fa-circle-info mr-2"></i>
                Launching a sale sets the duration from **today 12:00 AM to tomorrow 11:59 PM (48 hours total)**. Any existing active flash sale will be replaced.
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold shadow-md hover:from-emerald-700 hover:to-teal-700 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 text-sm flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-bolt"></i>
                {loading ? 'Activating...' : 'Activate Flash Sale'}
              </button>
            </form>
          </div>
        </div>

        {/* Current Active Sale Sidebar Card */}
        <div className="space-y-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col justify-between h-full min-h-[350px]">
            <div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
                <i className="fa-solid fa-circle-play text-amber-500"></i>
                Active Sale Banner
              </h3>

              {activeSale ? (
                <div className="space-y-6">
                  {/* Card Display Preview */}
                  <div className="relative rounded-2xl overflow-hidden shadow-md group">
                    <img 
                      src={activeSale.image && activeSale.image.startsWith('http') ? activeSale.image : 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400'} 
                      alt="Banner Preview" 
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
                    <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-white">
                      <h4 className="text-sm font-extrabold text-emerald-400">FLASH SALE</h4>
                      <p className="text-xs font-bold leading-tight line-clamp-1">{activeSale.course_title}</p>
                      <p className="text-[9px] text-gray-300">Starts ₹{activeSale.flash_sale_price}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Course:</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200 text-right">{activeSale.course_title}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Promo Price:</span>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 text-lg">₹{activeSale.flash_sale_price}</span>
                    </div>
                  </div>

                  {/* Dynamic Timer Display */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl text-center space-y-2 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Time Remaining</p>
                    <div className="flex justify-center gap-4 text-gray-800 dark:text-gray-200">
                      <div>
                        <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{String(timeRemaining.hours).padStart(2, '0')}</span>
                        <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Hrs</p>
                      </div>
                      <span className="text-xl font-bold mt-0.5">:</span>
                      <div>
                        <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{String(timeRemaining.minutes).padStart(2, '0')}</span>
                        <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Min</p>
                      </div>
                      <span className="text-xl font-bold mt-0.5">:</span>
                      <div>
                        <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{String(timeRemaining.seconds).padStart(2, '0')}</span>
                        <p className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Sec</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-12 text-gray-400 space-y-3">
                  <i className="fa-solid fa-bolt-slash text-4xl text-gray-300 dark:text-gray-600"></i>
                  <p className="text-sm">No flash sale is currently running.</p>
                </div>
              )}
            </div>

            {activeSale && (
              <button
                onClick={handleCancel}
                disabled={loading}
                className="mt-6 w-full py-2.5 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/20 dark:hover:bg-red-950/40 rounded-xl font-bold text-xs transition-colors flex items-center justify-center gap-2 border border-red-200 dark:border-red-900"
              >
                <i className="fa-solid fa-trash-can"></i>
                Cancel Active Sale
              </button>
            )}
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
          <i className="fa-solid fa-clock-rotate-left text-gray-600"></i>
          Promotion History
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider">
                <th className="py-3 px-4">Course</th>
                <th className="py-3 px-4">Sale Price</th>
                <th className="py-3 px-4">Start Time</th>
                <th className="py-3 px-4">End Time</th>
                <th className="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-sm">
              {history.map((sale) => {
                const isActive = sale.active && new Date(sale.end_time) > new Date();
                return (
                  <tr key={sale.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-gray-800 dark:text-gray-200">{sale.course_title}</td>
                    <td className="py-3.5 px-4 font-bold text-emerald-600 dark:text-emerald-400">₹{sale.flash_sale_price}</td>
                    <td className="py-3.5 px-4 text-xs text-gray-500 dark:text-gray-400">{new Date(sale.start_time).toLocaleString()}</td>
                    <td className="py-3.5 px-4 text-xs text-gray-500 dark:text-gray-400">{new Date(sale.end_time).toLocaleString()}</td>
                    <td className="py-3.5 px-4">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400 animate-ping"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          Expired
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {history.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-400">
                    No flash sale promotions launched yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default FlashSaleManager;
